/**
 * Notes Service
 *
 * Service for managing polymorphic notes associated with multiple resources
 * (workout, workout exercise, completed workout, completed workout exercise).
 *
 * Backed by the Laravel endpoints:
 * - GET  /api/note/{noteable}
 * - POST /api/note/{noteable}
 *
 * The {noteable} route parameter is a hashid that encodes the resource ID
 * and type on the backend. This service expects the prepared hashid value
 * and does not perform any encoding/decoding itself.
 *
 * @module services/notes
 */

import { rethrowTyped } from './api/errors';
import { CrudService } from './crud.service';

import type { QueryParams } from '../types/api/query.types';
import type {
  Note,
  CreateNotePayload,
  NoteableHashId,
} from '../types/domain/notes.types';

// =============================================================================
// NOTES SERVICE CLASS
// =============================================================================

/**
 * NotesService - Handles polymorphic notes for multiple resources
 */
export class NotesService extends CrudService<Note> {
  constructor(cacheTTL: number = 5 * 60 * 1000) {
    super({
      baseUrl: '/note',
      enableCaching: true,
      cacheTTL,
    });
  }

  // ---------------------------------------------------------------------------
  // CORE NOTE OPERATIONS
  // ---------------------------------------------------------------------------

  /**
   * Fetch all notes for a given noteable hashid
   *
   * @param noteableHashId - Hashid representing the noteable model
   * @param params - Optional query params (reserved for future filtering)
   */
  public async getNotesFor(
    noteableHashId: NoteableHashId,
    params?: QueryParams
  ): Promise<Note[]> {
    if (!noteableHashId) {
      throw new Error('Noteable hash ID is required to fetch notes');
    }

    try {
      return await this.withContext({
        template: '/note/{noteable}',
        parameters: { noteable: noteableHashId },
        cacheNamespace: `note-${noteableHashId}`,
      }).getAll(params);
    } catch (error) {
      console.error(
        `Failed to fetch notes for noteable ${noteableHashId}:`,
        error
      );
      rethrowTyped(error, 'Unable to fetch notes. Please try again.');
    }
  }

  /**
   * Create a new note for a given noteable hashid
   *
   * @param noteableHashId - Hashid representing the noteable model
   * @param payload - Note creation payload
   */
  public async addNoteTo(
    noteableHashId: NoteableHashId,
    payload: CreateNotePayload
  ): Promise<Note> {
    if (!noteableHashId) {
      throw new Error('Noteable hash ID is required to create a note');
    }

    if (!payload?.note || !payload.note.trim()) {
      throw new Error('Note content is required');
    }

    try {
      const contextualService = this.withContext({
        template: '/note/{noteable}',
        parameters: { noteable: noteableHashId },
        cacheNamespace: `note-${noteableHashId}`,
      });

      const created = await contextualService.create(payload);
      return created;
    } catch (error) {
      console.error(
        `Failed to create note for noteable ${noteableHashId}:`,
        error
      );
      rethrowTyped(
        error,
        'Unable to create note. Please check your data and try again.'
      );
    }
  }
}

// =============================================================================
// SERVICE INSTANCE
// =============================================================================

/**
 * Default notes service instance
 * Pre-configured and ready to use throughout the application
 */
export const notesService = new NotesService();
